import { lazy, Suspense, useState } from "react";
import type { OutdoorInterest, OutdoorSpot } from "../../../shared/models";
import { MAX_RESORTS, RESORTS } from "../../../shared/mountains";
import { OUTDOOR_INTERESTS } from "../../../shared/outdoors";
import { useHousehold } from "../../app/useHousehold";
import { useUpdateSettings } from "../../data/queries";
import { useRivers } from "../../data/weatherQueries";
import { Icon } from "../../ui/Icon";

const LocationMap = lazy(() => import("../../settings/LocationMap").then((m) => ({ default: m.LocationMap })));

/** What the household gets outside for, favorite places for mountain forecasts, and which river gauges the Outdoors screen shows. */
export function OutdoorsSection() {
  const { settings, isAdmin } = useHousehold();
  const update = useUpdateSettings();
  const rivers = useRivers();
  const [pick, setPick] = useState<{ lat: number; lon: number } | null>(null);
  const [name, setName] = useState("");
  const [sites, setSites] = useState(settings.riverSites.join(", "));
  const [msg, setMsg] = useState<string | null>(null);
  const home = settings.location ?? { lat: 39.74, lon: -105.0 };
  const spots = settings.outdoorSpots;

  const saveSpots = (next: OutdoorSpot[], done?: () => void) =>
    update.mutate({ outdoorSpots: next }, { onSuccess: () => (setMsg(null), done?.()), onError: (e) => setMsg((e as Error).message) });
  const add = () => {
    if (!pick || !name.trim()) return;
    saveSpots([...spots, { id: `spot_${Date.now().toString(36)}`, name: name.trim(), lat: pick.lat, lon: pick.lon }], () => {
      setPick(null);
      setName("");
    });
  };
  const toggleInterest = (k: OutdoorInterest) => {
    const has = settings.outdoorInterests.includes(k);
    update.mutate({ outdoorInterests: has ? settings.outdoorInterests.filter((x) => x !== k) : [...settings.outdoorInterests, k] }, { onError: (e) => setMsg((e as Error).message) });
  };
  const saveSites = () => {
    const list = sites
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    update.mutate({ riverSites: list }, { onSuccess: () => setMsg("River gauges saved"), onError: (e) => setMsg((e as Error).message) });
  };

  return (
    <div className="form">
      <h3>Outdoors</h3>
      <p className="muted">The Outdoors screen is built around a topographic trail map of home and your favorite places, with conditions for what your household does outside, the rivers near home, and fire danger.</p>

      <h4>What we get out for</h4>
      <p className="muted small">Each one gets its own line on the “Out there” card: trail mud for biking, storm timing for hiking, legal light and wind for hunting, solunar periods and water for fishing, and so on.</p>
      <div className="chip-row wrap" role="group" aria-label="Outdoor interests">
        {OUTDOOR_INTERESTS.map((i) => {
          const on = settings.outdoorInterests.includes(i.key);
          return (
            <button key={i.key} className={`chip ${on ? "on" : ""}`} aria-pressed={on} disabled={!isAdmin || update.isPending} onClick={() => toggleInterest(i.key)}>
              {on && <Icon name="check" size="1rem" />}
              {i.label}
            </button>
          );
        })}
      </div>

      <hr />
      <h4>Our mountains</h4>
      <p className="muted small">
        Up to {MAX_RESORTS} ski areas for the Mountains card and the Resorts screen: new snow at each one’s snow gauge, the snowfall forecast, the drive from home and chain or traction laws on the way. A powder day shows on
        Home and Morning.
      </p>
      <div className="chip-row wrap" role="group" aria-label="Ski areas">
        {RESORTS.map((r) => {
          const on = settings.mountains.resorts.includes(r.id);
          const full = !on && settings.mountains.resorts.length >= MAX_RESORTS;
          return (
            <button
              key={r.id}
              className={`chip ${on ? "on" : ""}`}
              aria-pressed={on}
              disabled={!isAdmin || update.isPending || full}
              onClick={() =>
                update.mutate(
                  { mountains: { ...settings.mountains, resorts: on ? settings.mountains.resorts.filter((x) => x !== r.id) : [...settings.mountains.resorts, r.id] } },
                  { onError: (e) => setMsg((e as Error).message) },
                )
              }
            >
              {on && <Icon name="check" size="1rem" />}
              {r.name}
            </button>
          );
        })}
      </div>
      <div className="field">
        <span>Powder day at</span>
        <div className="chip-row">
          {[4, 6, 8, 10, 12].map((n) => (
            <button
              key={n}
              className={`chip ${settings.mountains.powderInches === n ? "on" : ""}`}
              disabled={!isAdmin || update.isPending}
              onClick={() => update.mutate({ mountains: { ...settings.mountains, powderInches: n } }, { onError: (e) => setMsg((e as Error).message) })}
            >
              {n}″+
            </button>
          ))}
        </div>
        <p className="muted small">New snow overnight at the gauge, or that much forecast for the next 24 hours.</p>
      </div>

      <hr />
      <h4>Our places</h4>
      {spots.length === 0 ? (
        <p className="muted small">No places yet. Tap the map to add one.</p>
      ) : (
        <ul className="spot-admin">
          {spots.map((s) => (
            <li key={s.id}>
              <Icon name="mountain" size="1.1rem" />
              <strong>{s.name}</strong>
              <span className="muted small">
                {s.lat.toFixed(3)}, {s.lon.toFixed(3)}
              </span>
              {isAdmin && (
                <button className="icon-btn" onClick={() => saveSpots(spots.filter((x) => x.id !== s.id))} aria-label={`Remove ${s.name}`}>
                  <Icon name="trash" size="1.1rem" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {isAdmin && spots.length < 8 && (
        <>
          <Suspense fallback={<div className="location-map" />}>
            <LocationMap lat={pick?.lat ?? home.lat} lon={pick?.lon ?? home.lon} zoom={8} onPick={(lat, lon) => setPick({ lat: Math.round(lat * 1000) / 1000, lon: Math.round(lon * 1000) / 1000 })} />
          </Suspense>
          {pick ? (
            <div className="row">
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} maxLength={40} placeholder="Name, e.g. Eldora or Brainard Lake" autoFocus />
              <button className="btn btn-primary" onClick={add} disabled={!name.trim() || update.isPending}>
                Add place
              </button>
              <button className="btn" onClick={() => setPick(null)}>
                Cancel
              </button>
            </div>
          ) : (
            <p className="muted small">Tap the map where the place is, then give it a name.</p>
          )}
        </>
      )}

      <hr />
      <h4>River gauges</h4>
      <p className="muted small">
        Leave empty to show the gauges nearest home. To choose your own, enter USGS site numbers (find them at waterdata.usgs.gov), separated by commas.
      </p>
      {rivers.data && rivers.data.gauges.length > 0 && (
        <ul className="spot-admin">
          {rivers.data.gauges.map((g) => (
            <li key={g.siteId}>
              <Icon name="waves" size="1.1rem" />
              <strong>{g.name}</strong>
              <span className="muted small">{g.siteId}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <input className="input" value={sites} onChange={(e) => setSites(e.target.value)} disabled={!isAdmin} placeholder="Nearest to home" aria-label="USGS site numbers" />
        {isAdmin && (
          <button className="btn" onClick={saveSites} disabled={update.isPending}>
            Save gauges
          </button>
        )}
      </div>
      {msg && <p className={msg.endsWith("saved") ? "muted" : "error-text"}>{msg}</p>}
    </div>
  );
}
